import React, { useState, useMemo, useEffect, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ListLayout from '../../Generic/ListLayout';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useFetchPaginatedDataWithFilters from '../../../hooks/useFetchPaginatedDataWithFilters';
import DataTablePagination from '../../Generic/DataTablePagination';
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { createNewGeneralColumn, createDateColumn } from '../../Generic/dataGridColumns';
import { useSnackbar } from 'notistack';
import { useStore } from '../../../stores/store';
import { useSetMenu } from "../../../hooks/useSetMenu";
import AuthContext from "../../../context/AuthProvider";

const SchedulePlanWeekListByPlanId = () => {
  const { mainStore } = useStore();
  const { auth } = useContext(AuthContext);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isReplacement = searchParams.get('isReplacement') === 'true';

  // Set menu based on whether this is replacement schedule or regular schedule
  useSetMenu(
    isReplacement ? mainStore.ReplacementScheduleMenuType : mainStore.SchedulePlanMenuType,
    isReplacement ? mainStore.ReplacementScheduleMenuNo : mainStore.SchedulePlanMenuNo
  );

  const { t } = useTranslation();
  const { schedulePlanId } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const localizedTextsMap = useLocalizedTextsMap();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Build base endpoint without query params (hook will add them)
  const endpoint = `/StaffPortalStaffs/GetSchedulePlanWeeksByPlanId/${schedulePlanId}`;

  // Build filter params for staff-specific filtering
  const filterParams = useMemo(() => {
    let params = '';

    if (auth?.userId) {
      params += `&staffId=${auth.userId}`;

      // If coming from replacement schedule, show only replacement weeks
      // If coming from regular schedule, show only non-replacement weeks
      if (isReplacement) {
        params += `&isReplacementOnly=true`;
      } else {
        params += `&isReplacementOnly=false`;
      }
    }

    return params;
  }, [isReplacement, auth?.userId]);

  // Start session function with session type support
  // SessionType: 1 = Start (beginning of lecture), 2 = End (end of lecture)
  const startSession = async (schedulePlanWeekId, sessionType = 1) => {
    try {
      const response = await axiosPrivate.post(`/SchedulePlanWeekSessions/StartSession/${schedulePlanWeekId}`, {
        ForceNew: false,
        SessionType: sessionType
      });
      const { token, sessionId } = response.data;
      navigate(`/attendance/qr?token=${token}&sessionId=${sessionId}&schedulePlanWeekId=${schedulePlanWeekId}&sessionType=${sessionType}`);
    } catch (error) {
      console.error("Error starting session:", error);

      // Handle timing validation errors from backend
      if (error.response?.data?.errorCode) {
        const errorCode = error.response.data.errorCode;
        let errorMessage = t("errorStartingSession");

        switch (errorCode) {
          case 'TOO_EARLY_START':
            errorMessage = t("tooEarlyToStartSession");
            break;
          case 'TOO_LATE_START':
            errorMessage = t("tooLateToStartSession");
            break;
          case 'TOO_EARLY_END':
            errorMessage = t("tooEarlyToEndSession");
            break;
          case 'TOO_LATE_END':
            errorMessage = t("tooLateToEndSession");
            break;
          default:
            errorMessage = error.response.data.message || t("sessionTimingError");
        }

        enqueueSnackbar(errorMessage, { variant: "warning" });
      } else {
        enqueueSnackbar(t("errorStartingSession"), { variant: "error" });
      }
    }
  };

  // Use your custom hook to fetch paginated data.
  const { data: rawData, totalRecords } = useFetchPaginatedDataWithFilters(
    axiosPrivate,
    endpoint,
    page + 1,
    pageSize,
    searchQuery,
    filterParams
  );

  // State to store which schedule plan weeks have active sessions (by type)
  const [weeksWithSessions, setWeeksWithSessions] = useState({});
  const [activeSessionsInfo, setActiveSessionsInfo] = useState({});

  // Fetch session information for all weeks (including active start/end sessions)
  useEffect(() => {
    if (rawData && rawData.length > 0) {
      // Get all week IDs
      const weekIds = rawData.map(week => week.id);

      // Create batch requests for both session existence and active sessions
      Promise.all(weekIds.map(weekId =>
        Promise.all([
          axiosPrivate.get(`/SchedulePlanWeekSessions/CheckSessionExists/${weekId}`)
            .catch(() => ({ data: { exists: false } })),
          axiosPrivate.get(`/SchedulePlanWeekSessions/ActiveSessions/${weekId}`)
            .catch(() => ({ data: { hasStartSession: false, hasEndSession: false } }))
        ])
      )).then(results => {
        // Create mappings for week IDs
        const sessionMap = {};
        const activeMap = {};
        results.forEach((result, index) => {
          const weekId = weekIds[index];
          const [existsResult, activeResult] = result;
          sessionMap[weekId] = existsResult.data?.exists || false;
          activeMap[weekId] = {
            hasStartSession: activeResult.data?.hasStartSession || false,
            hasEndSession: activeResult.data?.hasEndSession || false,
            startSession: activeResult.data?.startSession || null,
            endSession: activeResult.data?.endSession || null,
            startSessionEverCreated: activeResult.data?.startSessionEverCreated || false
          };
        });
        setWeeksWithSessions(sessionMap);
        setActiveSessionsInfo(activeMap);
      });
    }
  }, [rawData, axiosPrivate]);
  
  // Process the data: enrich with session info AND sort ascending by week day
  // so the list reads oldest → newest (matches the academic timeline).
  const data = useMemo(() => {
    if (!rawData) return [];

    return rawData
      .map(item => ({
        ...item,
        hasSession: weeksWithSessions[item.id] || false,
        sessionInfo: activeSessionsInfo[item.id] || { hasStartSession: false, hasEndSession: false, startSessionEverCreated: false }
      }))
      .sort((a, b) => {
        const aTime = a.weekDay ? new Date(a.weekDay).getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = b.weekDay ? new Date(b.weekDay).getTime() : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      });
  }, [rawData, weeksWithSessions, activeSessionsInfo]);

  // Backend returns subjectName on every row (same value for all rows of this plan)
  // — read once for the card title to avoid extra requests.
  const subjectName = rawData?.[0]?.subjectName || '';

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const pastSessions = data ? data.filter(session => new Date(session.weekDay) < now) : [];
  const lastPastSessionDate =
    pastSessions.length > 0
      ? new Date(Math.max(...pastSessions.map(session => new Date(session.weekDay).getTime())))
      : null;

  // Add a new column for "View Attendance" button
  const viewAttendanceColumn = {
    field: 'viewAttendance',
    headerName: t("viewAttendance"),
    width: 150,
    headerAlign: "center",
    align: "center",
    headerClassName: 'tableHeaderClassName',
    cellClassName: 'tableCellClassName',
    renderHeader: (params) => (
      <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
  ),
    renderCell: (params) => {
      // Check if there's any attendance data for this week
      // We'll add state to track sessions with attendance data
      const hasAttendanceData = params.row.hasSession || false;
      
      // Set tooltip text based on availability
      const tooltipText = hasAttendanceData 
        ? t("viewAttendance") 
        : t("noSessionCreatedYet");
      
      return (
        <span 
          onClick={() => { 
            if (hasAttendanceData) {
              navigate(`/attendance/studentlist?schedulePlanWeekId=${params.row.id}`);
            }
          }}
          style={{
            padding: '5px 10px', 
            cursor: hasAttendanceData ? 'pointer' : 'not-allowed',
            opacity: hasAttendanceData ? 1 : 0.5
          }}
          className="text-brand"
          title={tooltipText}
        >
          {t("viewAttendance")}
        </span>
      );
    }
  };

  const columns = useMemo(() => [
    {
      field: 'attendance',
      headerName: t("attendance"),
      width: 170,
      headerAlign: "center",
      align: "center",
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderHeader: (params) => (
        <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
      ),
      renderCell: (params) => {
        // Get the date from the row data
        const weekDayStr = params.row.weekDay;
        const scheduleDate = new Date(weekDayStr);
        const today = new Date();

        // Normalize both dates to midnight for comparison
        scheduleDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        // Check if the schedule date is today
        const isCurrentDate = scheduleDate.getTime() === today.getTime();
        const isPastDate = scheduleDate < today;
        const isFutureDate = scheduleDate > today;

        // Only enable the button for the current date
        const isEnabled = isCurrentDate;

        // Get active session info
        const sessionInfo = params.row.sessionInfo || { hasStartSession: false, hasEndSession: false, startSessionEverCreated: false };
        const hasActiveStartSession = sessionInfo.hasStartSession;
        const hasActiveEndSession = sessionInfo.hasEndSession;
        const startSessionEverCreated = sessionInfo.startSessionEverCreated;

        // End button should be disabled if:
        // 1. Not current date, OR
        // 2. Start session has never been created
        const isEndEnabled = isEnabled && startSessionEverCreated;

        // Set tooltip text based on date and session state
        let startTooltip = "";
        let endTooltip = "";
        if (isPastDate) {
          startTooltip = t("cannotStartSessionForPastDate");
          endTooltip = t("cannotStartSessionForPastDate");
        } else if (isFutureDate) {
          startTooltip = t("cannotStartSessionForFutureDate");
          endTooltip = t("cannotStartSessionForFutureDate");
        } else {
          startTooltip = hasActiveStartSession ? t("startSessionActive") : t("startSessionBeginning");
          // Show different tooltip for End button if start session not created
          if (!startSessionEverCreated) {
            endTooltip = t("startSessionFirst");
          } else {
            endTooltip = hasActiveEndSession ? t("endSessionActive") : t("startSessionEnd");
          }
        }

        return (
          <div className="d-flex gap-2 align-items-center">
            {/* Start Session Button */}
            <button
              className={`btn btn-sm ${hasActiveStartSession ? 'btn-brand' : 'btn-outline-brand'}`}
              onClick={() => { if (isEnabled) startSession(params.row.id, 1); }}
              disabled={!isEnabled}
              title={startTooltip}
              style={{ minWidth: '70px' }}
            >
              <i className="bi bi-play-circle me-1"></i>
              {t("start")}
            </button>
            {/* End Session Button - disabled if no start session ever created */}
            <button
              className={`btn btn-sm ${hasActiveEndSession ? 'btn-brand' : 'btn-outline-brand'}`}
              onClick={() => { if (isEndEnabled) startSession(params.row.id, 2); }}
              disabled={!isEndEnabled}
              title={endTooltip}
              style={{ minWidth: '70px' }}
            >
              <i className="bi bi-stop-circle me-1"></i>
              {t("end")}
            </button>
          </div>
        );
      }
    },
    viewAttendanceColumn,
    // Existing columns:
    {
      field: 'weekDay',
      headerName: t("Week Day"),
      width: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderHeader: (params) => (
        <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
      ),
      renderCell: (params) => {
        const wd = params.row.weekDay ? new Date(params.row.weekDay) : null;
        return (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ lineHeight: 1.25, padding: '6px 0', gap: '2px', width: '100%' }}
          >
            <span style={{ fontWeight: 500 }}>
              {wd ? wd.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
            </span>
            {params.row.replacedSchedulePlanWeekId && params.row.replacedWeekDay && (
              <>
                <span
                  className="badge bg-info text-dark"
                  style={{ fontSize: '0.6rem', fontWeight: 500, padding: '2px 6px' }}
                  title={params.row.replacementReasonName || ''}
                >
                  ↺ {t("replaces") || "Replaces"} {new Date(params.row.replacedWeekDay).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </span>
                {params.row.replacementReasonName && (
                  <span
                    className="text-muted text-truncate"
                    style={{
                      fontSize: '0.6rem',
                      fontStyle: 'italic',
                      maxWidth: '170px',
                      lineHeight: 1.2
                    }}
                    title={params.row.replacementReasonName}
                  >
                    {params.row.replacementReasonName}
                  </span>
                )}
              </>
            )}
            {params.row.isReplaced && (
              <span className="badge bg-dark" style={{ fontSize: '0.6rem', fontWeight: 500, padding: '2px 6px' }}>
                {t("statusReplaced") || "Replaced"}
              </span>
            )}
          </div>
        );
      }
    },
    createNewGeneralColumn("scheduleTimeSlot", "Time Slot", t, 110),
    createNewGeneralColumn("branchObject", "Branch", t, 100),
    createNewGeneralColumn("room", "Room", t, 130),
    createNewGeneralColumn("groupPlanNames", "Groups", t, 100),
  ], [t, startSession, lastPastSessionDate, navigate]);

  return (
    <ListLayout
      title={subjectName ? `${t("Schedule Plan Weeks")} — ${subjectName}` : t("Schedule Plan Weeks")}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
    >
      <DataTablePagination
        rows={data}
        columns={columns}
        rowCount={totalRecords}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        localeTextsMap={localizedTextsMap}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 60}
      />
    </ListLayout>
  );
};

export default observer(SchedulePlanWeekListByPlanId);


// import React, { useState, useMemo } from 'react';
// import { observer } from 'mobx-react-lite';
// import { useTranslation } from 'react-i18next';
// import { useParams, useNavigate } from 'react-router-dom';
// import ListLayout from '../../Generic/ListLayout';
// import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
// import useFetchPaginatedDataWithFilters from '../../../hooks/useFetchPaginatedDataWithFilters';
// import DataTablePagination from '../../Generic/DataTablePagination';
// import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
// import { createNewGeneralColumn, createDateColumn, createQRCodeActionColumn } from '../../Generic/dataGridColumns';
// import { useSnackbar } from 'notistack';
// import { useStore } from '../../../stores/store';
// import { useSetMenu } from "../../../hooks/useSetMenu";

// const SchedulePlanWeekListByPlanId = () => {
//   const { mainStore } = useStore();
//   useSetMenu(mainStore.SchedulePlanMenuType, mainStore.SchedulePlanMenuNo);
//   const { t } = useTranslation();
//   const { schedulePlanId } = useParams(); 
//   const axiosPrivate = useAxiosPrivate();
//   const { enqueueSnackbar } = useSnackbar();
//   const localizedTextsMap = useLocalizedTextsMap();
//   const navigate = useNavigate(); 

//   const [searchQuery, setSearchQuery] = useState("");
//   const [page, setPage] = useState(0); 
//   const [pageSize, setPageSize] = useState(50);

//   const endpoint = `/StaffPortalStaffs/GetSchedulePlanWeeksByPlanId/${schedulePlanId}`;

//   // Define the startSession function
//   const startSession = async (schedulePlanWeekId) => {
//     try {
//       if (navigator.geolocation) {
//         navigator.geolocation.getCurrentPosition(async (position) => {
//           const { latitude, longitude } = position.coords;
//           // Call the backend endpoint to start a session for this lecture (schedule week)
//           const response = await axiosPrivate.post(`/SchedulePlanWeekSessions/StartSession/${schedulePlanWeekId}`, {
//             Latitude: latitude,
//             Longitude: longitude,
//             ForceNew: false
//           });
//           // Destructure using lower-case property names (as returned by the backend)
//           const { token, sessionId } = response.data;
//           // Navigate to the QR code page with token, sessionId, and schedulePlanWeekId in query parameters
//           navigate(`/attendance/qr?token=${token}&sessionId=${sessionId}&schedulePlanWeekId=${schedulePlanWeekId}`);
          
//         }, (error) => {
//           console.error("Geolocation error:", error);
//           enqueueSnackbar("Geolocation permission denied or unavailable", { variant: "error" });
//         });
//       } else {
//         enqueueSnackbar("Geolocation is not supported by this browser", { variant: "error" });
//       }
//     } catch (error) {
//       console.error("Error starting session:", error);
//       enqueueSnackbar("Error starting session", { variant: "error" });
//     }
//   };
  
//   // Use your custom hook to fetch paginated data.
//   const { data, totalRecords } = useFetchPaginatedDataWithFilters(
//     axiosPrivate,
//     endpoint,
//     page + 1,
//     pageSize,
//     searchQuery,
//     "" // no extra filter parameters
//   );

//   const handleSearchChange = (event) => {
//     setSearchQuery(event.target.value);
//     setPage(0);
//   };

//   const now = new Date();
//   now.setHours(0, 0, 0, 0); // normalize to midnight
  
//   const pastSessions = data ? data.filter(session => new Date(session.weekDay) < now) : [];
//   const lastPastSessionDate =
//     pastSessions.length > 0
//       ? new Date(Math.max(...pastSessions.map(session => new Date(session.weekDay).getTime())))
//       : null;

//   const columns = useMemo(() => [
//     {
//       field: 'startSession',
//       headerName: t("startSession"),
//       width: 150,
//       headerAlign: "center",
//       align: "center",
//       headerClassName: 'tableHeaderClassName',
//       cellClassName: 'tableCellClassName',
//       renderHeader: (params) => (
//         <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
//       ),
//       renderCell: (params) => {
//         // Create a Date object from the weekDay field.
//         const scheduleDate = new Date(params.row.weekDay);
//         scheduleDate.setHours(0, 0, 0, 0); // normalize to midnight
  
//         // Calculate the Monday of the scheduled week.
//         const scheduleMonday = new Date(scheduleDate);
//         let scheduleDay = scheduleMonday.getDay();
//         // JavaScript getDay() returns 0 for Sunday so adjust: Monday = 1, Sunday = 7.
//         scheduleDay = scheduleDay === 0 ? 7 : scheduleDay;
//         scheduleMonday.setDate(scheduleMonday.getDate() - (scheduleDay - 1));
//         scheduleMonday.setHours(0, 0, 0, 0);
  
//         // Calculate the Monday of the current week.
//         const today = new Date();
//         const currentMonday = new Date(today);
//         let currentDay = currentMonday.getDay();
//         currentDay = currentDay === 0 ? 7 : currentDay;
//         currentMonday.setDate(currentMonday.getDate() - (currentDay - 1));
//         currentMonday.setHours(0, 0, 0, 0);
  
//         const isEnabled =
//           (scheduleMonday.getTime() >= currentMonday.getTime()) ||
//           (lastPastSessionDate && scheduleDate.getTime() === lastPastSessionDate.getTime());
  
//         return (
//           <span 
//             onClick={() => { if(isEnabled) startSession(params.row.id); }}
//             style={{
//               padding: '5px 10px', 
//               cursor: isEnabled ? 'pointer' : 'not-allowed',
//               opacity: isEnabled ? 1 : 0.5
//             }}
//           >
//             <i className="bi bi-qr-code text-danger fs-4"></i>
//           </span>
//         );
//       }
//     },
//     createDateColumn("weekDay", "Week Day", t, 150),
//     createNewGeneralColumn("scheduleTimeSlot", "Time Slot", t, 150),
//     createNewGeneralColumn("branchObject", "Branch", t, 150),
//     createNewGeneralColumn("room", "Room", t, 150),
//     createNewGeneralColumn("groupPlanNames", "Groups", t, 200),
//   ], [t, startSession]);

//   return (
//     <ListLayout
//       title={t("Schedule Plan Weeks")}
//       searchQuery={searchQuery}
//       onSearchChange={handleSearchChange}
//     >
//       <DataTablePagination
//         rows={data}
//         columns={columns}
//         rowCount={totalRecords}
//         page={page}
//         setPage={setPage}
//         pageSize={pageSize}
//         setPageSize={setPageSize}
//         localeTextsMap={localizedTextsMap}
//       />
//     </ListLayout>
//   );
// };

// export default observer(SchedulePlanWeekListByPlanId);
